/**
 * Dropdown de la campana del header. Muestra:
 *
 * - Badge con número de PENDIENTES (rojo, sobre la campana).
 * - Lista compacta de los últimos N avisos.
 * - Link "Ver todos" a /avisos.
 *
 * Refresca contadores cada 60s (refetchInterval) y al ganar foco.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Loader2 } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AvisoCard from './AvisoCard';
import { getContadores, listAvisos, marcarLeido } from '../services/avisosService';

const DROPDOWN_PAGE_SIZE = 5;
const CONTADORES_REFRESH_MS = 60_000;

const NotificacionesDropdown: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  // Contadores — corre siempre (cada 60s) para el badge.
  const { data: contadores } = useQuery({
    queryKey: ['avisos-contadores'],
    queryFn: () => getContadores(),
    refetchInterval: CONTADORES_REFRESH_MS,
    refetchOnWindowFocus: true,
  });

  // Lista corta — solo se carga si el dropdown está abierto, para no
  // martillar el backend con el panel cerrado.
  const { data: avisos = [], isLoading } = useQuery({
    queryKey: ['avisos-dropdown'],
    queryFn: () => listAvisos({ estado: 'PENDIENTE', limit: DROPDOWN_PAGE_SIZE }),
    enabled: open,
  });

  // Cerrar al hacer click fuera.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Al abrir, marca como leídos los que se ven (no cambia estado, solo
  // graba fecha_lectura — el badge sigue contando PENDIENTES hasta que el
  // usuario los resuelva/descarte).
  useEffect(() => {
    if (!open || avisos.length === 0) return;
    const noLeidos = avisos.filter((a) => !a.fecha_lectura);
    if (noLeidos.length === 0) return;
    Promise.allSettled(noLeidos.map((a) => marcarLeido(a.id))).then(() => {
      // Sin invalidar — la lectura es informativa.
    });
  }, [open, avisos]);

  const total = contadores?.total ?? 0;
  const tieneAvisos = total > 0;

  const handleVerTodos = () => {
    setOpen(false);
    navigate('/avisos');
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        aria-label={tieneAvisos ? `Ver notificaciones (${total} pendientes)` : 'Ver notificaciones'}
        onClick={() => {
          setOpen((v) => !v);
          if (!open) {
            queryClient.invalidateQueries({ queryKey: ['avisos-dropdown'] });
          }
        }}
        className="relative p-2 text-brand-gray hover:bg-brand-ghost rounded-full group transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-tomato/40"
      >
        <Bell
          size={20}
          aria-hidden="true"
          className={tieneAvisos ? 'text-brand-tomato' : 'group-hover:text-brand-tomato'}
        />
        {tieneAvisos && (
          <span
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center text-[10px] font-bold text-white bg-brand-tomato rounded-full ring-2 ring-white"
            aria-hidden="true"
          >
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[360px] sm:w-[420px] max-h-[480px] flex flex-col bg-white rounded-xl shadow-xl border border-brand-silver/30 z-50">
          <div className="px-4 py-3 border-b border-brand-silver/20 flex items-center justify-between">
            <h3 className="font-bold text-brand-dark text-sm">
              Avisos pendientes
              {tieneAvisos && (
                <span className="ml-2 text-xs font-medium text-brand-gray">({total})</span>
              )}
            </h3>
          </div>

          <div className="flex-1 overflow-y-auto p-3 space-y-2">
            {isLoading ? (
              <div className="flex flex-col items-center py-8">
                <Loader2 size={20} className="animate-spin text-brand-tomato/60" />
                <p className="text-xs text-brand-gray mt-2">Cargando…</p>
              </div>
            ) : avisos.length === 0 ? (
              <div className="text-center py-8">
                <Bell size={24} className="mx-auto text-brand-silver/70 mb-2" />
                <p className="text-xs text-brand-gray">No tienes avisos pendientes.</p>
              </div>
            ) : (
              avisos.map((aviso) => <AvisoCard key={aviso.id} aviso={aviso} compact />)
            )}
          </div>

          <button
            type="button"
            onClick={handleVerTodos}
            className="px-4 py-3 border-t border-brand-silver/20 text-sm font-medium text-brand-tomato hover:bg-brand-tomato/5 transition-colors rounded-b-xl"
          >
            Ver todos los avisos →
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificacionesDropdown;
